import {
  addDoc,
  collection,
  doc,
  DocumentData,
  DocumentSnapshot,
  getDoc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore'
import moment from 'moment'
import { channel } from 'redux-saga'
import { ChannelTakeEffect, fork, put, select, SelectEffect, take, takeEvery } from 'redux-saga/effects'
import Notification from 'redux/models/notification'
import { AuthState } from 'redux/modules/auth'
import * as Notifications from 'redux/modules/notifications'
import { AppState } from 'redux/store'
import { firestore } from 'utils/firebaseApp'

type AddAction = ReturnType<typeof Notifications.actions.add>
type LoadEditItemAction = ReturnType<typeof Notifications.actions.loadEditItem>
type UpdateAction = ReturnType<typeof Notifications.actions.update>

const redirectChannel = channel()
const collectionRef = collection(firestore, 'notifications')

const selectState = <T>(selector: (s: AppState) => T): SelectEffect => {
  return select(selector)
}

// notifications の同期
const load = function* () {
  yield take(Notifications.LOAD)

  const q = query(collectionRef, where('isDeleted', '==', false))

  onSnapshot(q, (snapshot) => {
    let items: Notification[] = []
    snapshot.docs.forEach((doc) => {
      const item = Notification.load(doc.id, doc.data())
      item && items.push(item)
    })
    items = items.sort((a, b) => b.updatedAt.unix() - a.updatedAt.unix()) // 降順にソート
    redirectChannel.put(Notifications.actions.sync(items))
  })
}

const addNotification = function* (action: AddAction) {
  const auth: AuthState = yield selectState((s) => s.auth)
  const user = auth.user
  if (!user) return

  const fields = action.payload
  const timestamp = moment().unix()

  yield addDoc(collectionRef, {
    ...fields,
    userId: user.id,
    createdAt: timestamp,
    updatedAt: timestamp,
    isDeleted: false,
  })
}

const loadEditItem = function* (action: LoadEditItemAction) {
  const itemId = action.payload
  const item: DocumentSnapshot<DocumentData> = yield getDoc(doc(collectionRef, itemId))

  if (!item) {
    yield put(Notifications.actions.failedToLoadEditItem())
    return
  }

  const notification = Notification.load(itemId, item.data())

  if (notification) {
    yield put(Notifications.actions.succeededToLoadEditItem(notification))
  } else {
    yield put(Notifications.actions.failedToLoadEditItem())
  }
}

const updateNotification = function* (action: UpdateAction) {
  const notifications: Notifications.NotificationsState = yield selectState((s) => s.notifications)
  const { item } = notifications.edit
  const data = {
    ...action.payload,
    updatedAt: moment().unix(),
  }

  yield item && updateDoc(doc(collectionRef, item.id), data)

  yield put(Notifications.actions.updated())
}

const deleteNotification = function* () {
  const notifications: Notifications.NotificationsState = yield selectState((s) => s.notifications)
  const { item } = notifications.edit

  yield item &&
    updateDoc(doc(collectionRef, item.id), {
      isDeleted: true,
      updatedAt: moment().unix(),
    })

  yield put(Notifications.actions.deleted())
}

export default function* dataSaga() {
  yield fork(load)
  yield takeEvery(Notifications.ADD_ITEM, addNotification)
  yield takeEvery(Notifications.LOAD_EDIT_ITEM, loadEditItem)
  yield takeEvery(Notifications.UPDATE_ITEM, updateNotification)
  yield takeEvery(Notifications.DELETE_ITEM, deleteNotification)

  while (true) {
    const action: ChannelTakeEffect<unknown> = yield take(redirectChannel)
    yield put(action)
  }
}
